"use client";

import { Box, Flex, Text, Link } from "@chakra-ui/react";
import { css, Global } from "@emotion/react";
import NextLink from "next/link";
import { FC, PropsWithChildren } from "react";
import { useQuery } from "@apollo/client/react";
import gql from "graphql-tag";
import { Price } from "../../types/subscription";
import Button from "../Button";
import Logo from "./Header/Logo";

const GET_PRICE = gql`
  query GetPrice {
    price {
      id
      value
    }
  }
`;

const TwoColumnSplitLayout: FC<PropsWithChildren> = ({ children }) => {
  const { data: priceData } = useQuery<{ price: Price }>(GET_PRICE);

  const price = priceData?.price;

  return (
    <>
      <Global
        styles={css`
          body {
            background-color: #edf2f7;
          }
        `}
      />

      <Flex flexDirection={["column", null, null, "row"]}>
        <Flex
          position={{ lg: "sticky" }}
          h={{ lg: "100dvh" }}
          top={0}
          left={0}
          flexDirection="column"
          bg="#023648"
          py={6}
          px={4}
          minHeight={[null, null, null, "100vh"]}
          w={[null, null, null, "40%"]}
          minWidth={[null, null, null, 500]}
        >
          <Flex flexDirection="column" flexGrow={1} justifyContent="center">
            <Flex justifyContent="center" mb={[3, null, null, 12]}>
              <NextLink href="/">
                <Logo />
              </NextLink>
            </Flex>

            <Box mx="auto" w="fit-content" maxWidth={1600}>
              <Flex
                gap={[0, null, null, 10]}
                alignItems="center"
                flexDirection="column"
                textAlign="center"
                maxWidth="40ch"
              >
                <Box textAlign="center" p={5}>
                  <Button
                    asChild
                    visual="light"
                    w="300px"
                    size={["md", "lg"]}
                    color="#023648"
                  >
                    <NextLink href="/login">Log in</NextLink>
                  </Button>
                </Box>

                <Text fontSize={[14, 16, null, 18]} color="#fff">
                  Zero to Finals Members is an interactive study resource that
                  helps students and health professionals prepare for their
                  medical exams.
                </Text>

                <Box textAlign="center" p={5} borderRadius={15}>
                  <Text
                    fontSize={[14, 16, null, 18]}
                    color="white"
                    lineHeight={2}
                  >
                    Not already a Zero to Finals Member?
                    <br />
                    {price?.value && `Join for ${price.value}`}
                  </Text>
                  <Button
                    asChild
                    size={["md", "lg"]}
                    visual="primaryOutline"
                    color="white"
                    mt={[6, 4]}
                    mb={2}
                    _hover={{
                      bg: "rgba(255, 255, 255, 0.1)",
                    }}
                  >
                    <NextLink href="/register">Join here</NextLink>
                  </Button>
                </Box>
              </Flex>
            </Box>
          </Flex>

          <Flex gap={5} justifyContent="center" mt="auto" px={2}>
            {[
              ["/terms-and-conditions", "Terms and Conditions"],
              ["/terms-and-conditions#refund-policy", "Refund Policy"],
              ["/faq", "FAQ"],
            ].map(([url, label]) => (
              <Link
                key={url}
                as={NextLink}
                variant="underline"
                href={url}
                color="white"
                fontSize={["sm", null, null, "md"]}
              >
                {label}
              </Link>
            ))}
          </Flex>
        </Flex>

        <Box flexGrow={1} p={[5, 10, null, null, null, 20]} overflowY="auto">
          {children}
        </Box>
      </Flex>
    </>
  );
};

export default TwoColumnSplitLayout;
